/**
 * Storage configuration for Precision Clicker
 *
 * Simple localStorage wrapper for type-safe persistence.
 */

import type { Settings } from '../utils/types';

const STORAGE_PREFIX = 'precision-clicker:v1:';

function getItem<T>(key: string): T | null {
  const value = localStorage.getItem(STORAGE_PREFIX + key);
  if (value === null) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function setItem<T>(key: string, value: T): void {
  localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
}

/**
 * Storage schema for Precision Clicker
 */
export interface PrecisionClickerStorage {
  /** Highest score achieved */
  highScore: number;

  /** User preferences */
  settings: Settings;
}

/**
 * Game storage instance for Precision Clicker
 */
export const gameStorage = {
  get<K extends keyof PrecisionClickerStorage>(key: K): PrecisionClickerStorage[K] | null {
    return getItem<PrecisionClickerStorage[K]>(key as string);
  },
  set<K extends keyof PrecisionClickerStorage>(key: K, value: PrecisionClickerStorage[K]): void {
    setItem<PrecisionClickerStorage[K]>(key as string, value);
  }
};

/**
 * Helper: Load high score with default
 */
export function loadHighScore(): number {
  return gameStorage.get('highScore') ?? 0;
}

/**
 * Helper: Save high score
 */
export function saveHighScore(score: number): void {
  gameStorage.set('highScore', score);
}

/**
 * Helper: Load settings with defaults
 */
export function loadSettings(): Settings {
  const defaultSettings: Settings = {
    showMotivation: true,
    soundEnabled: false,
  };

  return gameStorage.get('settings') ?? defaultSettings;
}

/**
 * Helper: Save settings
 */
export function saveSettings(settings: Settings): void {
  gameStorage.set('settings', settings);
}
